import { FC, useMemo, useState } from "react"
import s from "../NewOrders.module.css"
import {
  IOrder,
  ISupply,
  addOrdersToSupplies,
  createNewSuppliesAndAddOrders,
  setNewOrders,
  updateSupplies,
} from "../../../app/reducers/ordersReducer"
import { useAppDispatch } from "../../../app/hooks"
import { Modal } from "../../../features/Modal/Modal"
import { CreateNewSupplies } from "./CreateNewSupplies"
import { AddToSupplies } from "./AddToSupplies"
import { StorageSelectControl } from "../../StorageSelect/StorageSelectControl"
import { MARKETPLACE } from "@acc/shared"
import { Button, Box } from "@radix-ui/themes"
import { DataTable, type DataTableColumn } from "../../DataTable/DataTable"

type WBNewOrdersProps = {
  orders: IOrder[]
  lastDateUpdatedSupplies?: string
  storages: {
    name: string
    officeId: number
    id: number
  }[]
  supplies: ISupply[]
}

const columns: DataTableColumn<IOrder>[] = [
  { key: "id", header: "№ задания" },
  {
    key: "createdAt",
    header: "Создано",
    cell: (el) => new Date(el.createdAt).toLocaleString(),
  },
  { key: "warehouseId", header: "Склад" },
  { key: "article", header: "Артикул продавца" },
  { key: "nmId", header: "Артикул WB" },
  {
    key: "convertedPrice",
    header: "Цена",
    cell: (el) => +el.convertedPrice / 100,
  },
]

export const WBNewOrders: FC<WBNewOrdersProps> = ({
  orders,
  storages,
  supplies,
  lastDateUpdatedSupplies,
}) => {
  const dispatch = useAppDispatch()
  const [selected, setSelected] = useState<IOrder[]>([])
  const [isOpenModalCreateSupplies, setOpenModalCreateSupplies] =
    useState(false)
  const [isOpenModalAddToSupplies, setOpenModalAddToSupplies] = useState(false)
  const [selectedStorageId, setSelectedStorages] = useState("")
  const [selectedSupplyId, setSelectedSupply] = useState("")

  let newSupplyName = `${
    storages.find((el) => +el.id === +selectedStorageId)?.name
  }-${new Date().toLocaleString()}`?.replace(", ", "-")

  const filteredOrders = orders.filter((ord) =>
    selectedStorageId ? +ord.warehouseId === +selectedStorageId : true,
  )

  const selectedKeys = useMemo(
    () => new Set(selected.map((o) => o.id)),
    [selected],
  )

  return (
    <Box px="4">
      <div className={s.orderContent}>
        <div className={s.table}>
          <StorageSelectControl
            label="Отфильтровать по складу"
            value={selectedStorageId}
            onChange={(value) => {
              setSelected([])
              setSelectedStorages(value)
            }}
            controlKey="storages-select"
            marketplace={MARKETPLACE.WB}
          />
          <DataTable
            columns={columns}
            data={filteredOrders}
            rowKey={(el, i) => el.article + "-" + i}
            rowClassName={() => s.rowValue}
            selectable
            selectedKeys={selectedKeys}
            onSelectionChange={setSelected}
            getRowId={(el) => el.id}
            selectedClassName={s.active}
          />
        </div>
        <div className={s.buttons}>
          <Button onClick={() => dispatch(setNewOrders())}>
            Обновить список заказов
          </Button>
          <Button
            disabled={!selected.length || !selectedStorageId}
            onClick={() => {
              if (selected.length) {
                setOpenModalCreateSupplies(true)
              }
            }}
          >
            Создать новую поставку и добавить в нее выбранные задания
          </Button>
          <Button
            disabled={!selected.length}
            onClick={() => setOpenModalAddToSupplies(true)}
          >
            Добавить задания в поставку
          </Button>
        </div>
      </div>
      <Modal
        isOpen={isOpenModalCreateSupplies}
        onClose={() => {
          setOpenModalCreateSupplies(false)
        }}
        header={"Создать новую поставку и добавить в нее выбранные задания"}
        onOk={() => {
          newSupplyName &&
            selectedStorageId &&
            orders.length &&
            dispatch(
              createNewSuppliesAndAddOrders({
                name: newSupplyName,
                orderIds: selected.map((el) => el.id),
              }),
            )
        }}
        content={
          <CreateNewSupplies orders={selected} newSupplyName={newSupplyName} />
        }
        size="auto"
      />
      <Modal
        isOpen={isOpenModalAddToSupplies}
        onClose={() => {
          setOpenModalAddToSupplies(false)
        }}
        header={"Добавить выбранные задания в существующую поставку"}
        onOk={() => {
          orders.length &&
            selectedSupplyId &&
            dispatch(
              addOrdersToSupplies({
                supplyId: +selectedSupplyId,
                orderIds: selected.map((el) => el.id),
              }),
            )
        }}
        content={
          <AddToSupplies
            orders={selected}
            supplies={supplies}
            lastDateUpdatedSupplies={lastDateUpdatedSupplies}
            updateSupplies={() => dispatch(updateSupplies())}
            selectedSupply={selectedSupplyId}
            setSelectedSupply={setSelectedSupply}
          />
        }
        size="auto"
        isOkDisabled={!selectedSupplyId}
      />
    </Box>
  )
}
